/**
 * MISC response PROJECTIONS — hand-composed from generated atoms.
 *
 * A grab-bag domain covering the smaller API surfaces: integrations (+ the
 * per-provider account lists), Instagram, notifications, billing, timesheets,
 * face groups, sequences, API keys, training hub, and the
 * embedded Stripe Connect status. Follow the pattern in ./leads.ts / ./sales.ts.
 *
 * Pure Zod, composed with `z.object` / `.extend` / `z.array` / `z.record`.
 * Money is integer cents on the wire (`z.number()`); dates are ISO strings
 * (atoms are already wire-shaped). `$type<>()` text/jsonb columns widen to
 * `z.string()` / `z.unknown()` in the atoms — narrowed back here to match the
 * api-client contract types.
 */
import { channelFixActionValues, channelRecordValues, channelStatusValues, connectionChannelValues, connectionHealthRemedyValues, connectionHealthStateValues, connectionProviderValues, integrationTypeValues, metaIntegrationStatusValues, notificationTypeValues, tokenStatusValues, } from '@borradh-workspace/labels';
import { importKindValues, importedRecordTypeValues, } from '@borradh-workspace/labels';
import { z } from 'zod';
import { apikeyAtomSchema, creditTransactionsAtomSchema, faceGroupAssetAtomSchema, faceGroupAtomSchema, invoicesAtomSchema, notificationAtomSchema, organizationIntegrationAtomSchema, sequenceAtomSchema, subscriptionsAtomSchema, timeEntryAtomSchema, timeEntryBreakAtomSchema, trainingVideoAtomSchema, userVideoProgressAtomSchema, } from '../generated/index.js';
// ============================================================================
// NOTIFICATIONS
// ============================================================================
/**
 * A notification feed row. The `notification` atom widens `type` (a
 * `$type<NotificationType>` text column) to `z.string()` and `data` (a typed
 * jsonb payload) to `z.unknown()`; both are narrowed back here to the contract.
 */
export const notificationSchema = notificationAtomSchema.extend({
    type: z.enum(notificationTypeValues),
    data: z.record(z.string(), z.unknown()).nullable(),
});
/** `GET /notifications` — the `{ items, total }` feed wrapper. */
export const listNotificationsResponseSchema = z.object({
    items: z.array(notificationSchema),
    total: z.number(),
});
/** `GET /notifications/unread-count` — the unread badge count. */
export const unreadNotificationCountResponseSchema = z.object({
    count: z.number(),
});
/** `POST /notifications/read-all` — the number of rows marked read. */
export const markAllNotificationsReadResponseSchema = z.object({
    updated: z.number(),
});
// ============================================================================
// BILLING — subscription / credits / invoices
// ============================================================================
/** The org subscription row (the atom, verbatim). */
export const subscriptionSchema = subscriptionsAtomSchema;
/** An invoice row (the atom, verbatim). Amounts are integer cents. */
export const invoiceSchema = invoicesAtomSchema;
/**
 * A credit-ledger entry. The atom widens `metadata` (jsonb) to `z.string()`; it
 * stays a string on the wire so no narrowing is needed.
 */
export const creditTransactionSchema = creditTransactionsAtomSchema;
/** `GET /billing/subscription` — the subscription wrapper (null = none). */
export const billingIssueSchema = z.object({
    status: z.string(),
    title: z.string(),
    message: z.string(),
    paymentFailureReason: z.string().nullable(),
    invoiceUrl: z.string().url().nullable().optional(),
    requiresVerification: z.boolean().optional(),
});
export const subscriptionResponseSchema = z.object({
    subscription: subscriptionSchema.nullable(),
    billingIssue: billingIssueSchema.nullable().optional(),
});
/**
 * `GET /billing/credits` — the credit balance response. A COMPUTED shape (not
 * the `credit_balances` atom): only a subset of columns plus derived
 * human-readable values (`creditsAvailable`, `includedMonthly`).
 */
export const creditBalanceResponseSchema = z.object({
    balance: z
        .object({
        id: z.string(),
        organizationId: z.string(),
        balance: z.number(),
        includedCredits: z.number(),
        purchasedCredits: z.number(),
        creditsAvailable: z.number(),
        includedMonthly: z.number(),
    })
        .nullable(),
});
/** A purchasable credit package (computed; no backing table). */
export const creditPackageSchema = z.object({
    id: z.string(),
    name: z.string(),
    credits: z.number(),
    priceInCents: z.number(),
    priceFormatted: z.string(),
    popular: z.boolean().optional(),
});
/** `GET /billing/credits/packages` — the purchasable packages list. */
export const creditPackagesResponseSchema = z.object({
    packages: z.array(creditPackageSchema),
});
/** Currency-specific price info attached to a billing plan. */
export const currencyPriceSchema = z.object({
    stripePriceId: z.string(),
    priceInCents: z.number(),
});
/** Supported subscription pricing currency. */
export const supportedCurrencySchema = z.enum(['usd', 'eur', 'gbp']);
/** A billing plan (computed static config; no backing table). */
export const billingPlanSchema = z.object({
    id: z.string(),
    name: z.string(),
    priceInCents: z.number(),
    includedCredits: z.number(),
    priceFormatted: z.string(),
    features: z.array(z.string()).optional(),
    currencyPrices: z
        .record(supportedCurrencySchema, currencyPriceSchema)
        .optional(),
});
/** `GET /billing/plan` — the current plan info. */
export const planInfoResponseSchema = z.object({
    plan: billingPlanSchema,
});
/** `GET /billing/currency` — the currency the Stripe customer is locked to. */
export const billingCurrencyResponseSchema = z.object({
    currency: supportedCurrencySchema.nullable(),
});
/**
 * `GET /billing/lapsed-ads` — Borradh-launched Meta campaigns still running
 * after the subscription ended. `notice` is null when there is nothing to show.
 */
export const lapsedAdsNoticeResponseSchema = z.object({
    notice: z
        .object({
        campaigns: z.array(z.object({
            metaCampaignId: z.string(),
            name: z.string(),
            pauseUrl: z.string(),
        })),
        pauseOn: z.string().nullable(),
    })
        .nullable(),
});
/** A Stripe Checkout session result (subscription / credits purchase). */
export const checkoutSessionResultSchema = z.object({
    sessionId: z.string(),
    url: z.string(),
});
/** A Stripe billing-portal session result. */
export const portalSessionResultSchema = z.object({
    url: z.string(),
});
// ============================================================================
// TIMESHEETS — time entries + breaks
// ============================================================================
/** A time-entry (clock-in/out) row — the atom, verbatim. */
export const timeEntrySchema = timeEntryAtomSchema;
/** A break row nested under a time entry — the atom, verbatim. */
export const timeEntryBreakSchema = timeEntryBreakAtomSchema;
/** `GET /time-entries` — a time entry with its nested breaks. */
export const timeEntryWithBreaksSchema = timeEntryAtomSchema.extend({
    breaks: z.array(timeEntryBreakSchema),
});
// ============================================================================
// FACE GROUPS
// ============================================================================
/** A face-group row — the atom, verbatim. */
export const faceGroupSchema = faceGroupAtomSchema;
/** A face-group ↔ asset link row — the atom, verbatim. */
export const faceGroupAssetSchema = faceGroupAssetAtomSchema;
/** A face-group asset with its joined asset display fields. */
export const faceGroupAssetWithDetailsSchema = z.object({
    id: z.string(),
    assetId: z.string(),
    role: z.enum(['before', 'after', 'untagged']),
    asset: z.object({
        id: z.string(),
        name: z.string(),
        blobUrl: z.string(),
        type: z.string(),
    }),
});
/** A face group with its assets (computed, joined projection). */
export const faceGroupWithAssetsSchema = z.object({
    leadId: z.string().nullable(),
    id: z.string(),
    organizationId: z.string(),
    clientName: z.string().nullable(),
    serviceId: z.string().nullable(),
    assets: z.array(faceGroupAssetWithDetailsSchema),
});
/**
 * `GET /face-groups/batch/:batchId` — the per-batch grouping result.
 *
 * Groups are now built only by manual before/after pairing, so there is no
 * asynchronous detection pass to wait on: `status` is always `complete`.
 */
export const batchFaceGroupsResponseSchema = z.object({
    faceGroups: z.array(faceGroupWithAssetsSchema),
    status: z.literal('complete'),
});
/** A face-group list item (computed: adds the joined `serviceName`). */
export const faceGroupListItemSchema = z.object({
    id: z.string(),
    clientName: z.string().nullable(),
    clientNotes: z.string().nullable(),
    thumbnailUrl: z.string().nullable(),
    serviceId: z.string().nullable(),
    serviceName: z.string().nullable(),
    totalDetections: z.number(),
    batchCount: z.number(),
    isExcluded: z.boolean(),
    createdAt: z.string(),
});
/** `GET /face-groups` — list projection: `{ items, total, limit, offset }`. */
export const listFaceGroupsResponseSchema = z.object({
    items: z.array(faceGroupListItemSchema),
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
});
/** A face-group asset with full asset metadata (`GET /face-groups/:id/assets`). */
export const faceGroupAssetItemSchema = z.object({
    id: z.string(),
    assetId: z.string(),
    role: z.enum(['before', 'after', 'untagged']),
    asset: z.object({
        id: z.string(),
        name: z.string(),
        blobUrl: z.string(),
        type: z.string(),
        duration: z.number().nullable(),
        width: z.number().nullable(),
        height: z.number().nullable(),
        createdAt: z.string(),
    }),
});
/** `GET /face-groups/:id/assets` — the face group plus its detailed assets. */
export const getFaceGroupAssetsResponseSchema = z.object({
    faceGroup: z.object({
        id: z.string(),
        clientName: z.string().nullable(),
        thumbnailUrl: z.string().nullable(),
        serviceId: z.string().nullable(),
    }),
    assets: z.array(faceGroupAssetItemSchema),
});
// ============================================================================
// INTEGRATIONS — the generic integration + per-provider account lists
// ============================================================================
/** An organization-integration row — the atom, verbatim. */
export const organizationIntegrationSchema = organizationIntegrationAtomSchema;
/**
 * The simplified integration view returned by `GET /integrations`. A COMPUTED
 * shape (not the atom): typed `config` and a derived `status`.
 */
export const integrationSchema = z.object({
    id: z.string(),
    type: z.enum(integrationTypeValues),
    name: z.string(),
    status: z.enum(['active', 'inactive', 'error']),
    config: z.record(z.string(), z.unknown()),
    lastSyncAt: z.string().optional(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** `GET /integrations` — `{ integrations, total }`. */
export const listIntegrationsResponseSchema = z.object({
    integrations: z.array(integrationSchema),
    total: z.number(),
});
/**
 * A connected calendar account (computed provider view).
 *
 * `displayName`/`lastSyncAt`/`tokenExpiresAt` are raw nullable columns and
 * `connectedByName` a nullable `leftJoin` on `user`
 * (`packages/features/src/integrations/services/list-calendar-accounts/list-calendar-accounts.service.ts`
 * selects all four straight through, no `?? undefined` fallback) —
 * `.nullish()` so a `null` value survives the interceptor's JSON round-trip
 * (ENG-843).
 */
export const calendarAccountSchema = z.object({
    id: z.string(),
    email: z.string(),
    displayName: z.string().nullish(),
    calendarId: z.string(),
    isActive: z.boolean(),
    syncEnabled: z.boolean(),
    purpose: z.enum(['sync', 'meet_only']).optional(),
    syncScope: z.enum(['organization', 'personal']),
    selected: z.boolean(),
    userId: z.string(),
    lastSyncAt: z.string().nullish(),
    tokenExpiresAt: z.string().nullish(),
    createdAt: z.string(),
    connectedByName: z.string().nullish(),
});
/** `GET /integrations/calendar/accounts` — `{ accounts }`. */
export const listCalendarAccountsResponseSchema = z.object({
    accounts: z.array(calendarAccountSchema),
    available: z.boolean().optional(),
});
/** A connected WhatsApp account (computed provider view). */
export const whatsAppAccountSchema = z.object({
    id: z.string(),
    phoneNumberId: z.string(),
    phoneNumber: z.string(),
    displayName: z.string().nullable(),
    isActive: z.boolean(),
    isVerified: z.boolean(),
    isChatbotActive: z.boolean(),
    connectedByName: z.string().nullable(),
    tokenExpiresAt: z.string().nullable(),
    tokenStatus: z.enum(['valid', 'needs_reconnect']),
    createdAt: z.string(),
});
/** `GET /integrations/whatsapp/accounts` — `{ accounts }`. */
export const listWhatsAppAccountsResponseSchema = z.object({
    accounts: z.array(whatsAppAccountSchema),
});
/**
 * A connected Meta Ads page as returned by `GET /integrations/meta-ads/pages`
 * (the `MetaAdsPageInfo` service shape). Standalone Instagram integrations are
 * folded in as virtual page entries, hence the `linkedInstagram*` fields.
 *
 * NOTE: this is a DIFFERENT shape from the pages nested on the integration
 * detail (`metaIntegrationPageSchema` below) — the two endpoints project
 * different columns.
 */
export const metaAdsPageSchema = z.object({
    id: z.string(),
    pageId: z.string(),
    pageName: z.string().nullable(),
    platform: z.enum(['facebook', 'instagram']),
    pixelId: z.string().nullable(),
    pixelName: z.string().nullable(),
    defaultLeadFormId: z.string().nullable(),
    defaultLeadFormName: z.string().nullable(),
    linkedInstagramAccountId: z.string().nullable(),
    linkedInstagramUsername: z.string().nullable(),
    linkedInstagramName: z.string().nullable(),
    isChatbotActive: z.boolean(),
    isActive: z.boolean(),
    isDefault: z.boolean(),
    createdAt: z.string(),
});
/** `GET /integrations/meta-ads/pages` — `{ pages }`. */
export const listMetaAdsPagesResponseSchema = z.object({
    pages: z.array(metaAdsPageSchema),
});
/**
 * A page nested on the Meta Ads integration detail (`MetaPageInfo`). Unlike the
 * list-pages shape it carries the page profile + default-ad-account columns and
 * omits the chatbot / isDefault fields.
 */
export const metaIntegrationPageSchema = z.object({
    id: z.string(),
    pageId: z.string(),
    pageName: z.string().nullable(),
    pageUsername: z.string().nullable(),
    pagePictureUrl: z.string().nullable(),
    platform: z.enum(['facebook', 'instagram']),
    pixelId: z.string().nullable(),
    pixelName: z.string().nullable(),
    defaultLeadFormId: z.string().nullable(),
    defaultLeadFormName: z.string().nullable(),
    defaultAdAccountId: z.string().nullable(),
    defaultAdAccountName: z.string().nullable(),
    defaultAdAccountCurrency: z.string().nullable(),
    linkedInstagramAccountId: z.string().nullable(),
    linkedInstagramUsername: z.string().nullable(),
    linkedInstagramName: z.string().nullable(),
    isActive: z.boolean(),
    createdAt: z.string(),
});
/**
 * Wizard option blobs (jsonb `$type<…Stored>`) surfaced only while the
 * integration is in `pending_selection` — otherwise null.
 */
export const metaBusinessInfoSchema = z.object({
    id: z.string(),
    name: z.string(),
    profilePictureUri: z.string().optional(),
});
export const metaAdAccountInfoSchema = z.object({
    id: z.string(),
    accountId: z.string(),
    name: z.string(),
    currency: z.string(),
    accountStatus: z.number(),
    businessName: z.string().optional(),
    businessId: z.string().optional(),
});
// The stored row also carries the Page access token; the API never sends it
// to the browser (getMetaIntegration strips it).
export const metaPageInfoStoredSchema = z.object({
    id: z.string(),
    name: z.string(),
    category: z.string().optional(),
    pictureUrl: z.string().optional(),
    businessId: z.string().optional(),
    businessName: z.string().optional(),
    instagramBusinessAccount: z
        .object({
        id: z.string(),
        // Meta can omit the Instagram display name even for a connected account.
        name: z.string().nullish(),
        username: z.string(),
        profilePictureUrl: z.string().optional(),
    })
        .optional(),
});
/**
 * A Meta Ads integration (`MetaIntegrationInfo`) with its connected pages,
 * default page, derived `tokenStatus`, the connecting user's profile, and the
 * wizard option blobs (populated only in `pending_selection`).
 */
export const metaAdsIntegrationSchema = z.object({
    id: z.string(),
    configurationStatus: z.enum(metaIntegrationStatusValues),
    adAccountId: z.string().nullable(),
    adAccountName: z.string().nullable(),
    defaultPageId: z.string().nullable(),
    isActive: z.boolean(),
    tokenStatus: z.enum(tokenStatusValues),
    connectedByName: z.string().nullable(),
    facebookUserName: z.string().nullable(),
    facebookUserEmail: z.string().nullable(),
    facebookUserPictureUrl: z.string().nullable(),
    tokenExpiresAt: z.string().nullable(),
    createdAt: z.string(),
    lastSyncAt: z.string().nullable(),
    lastSyncError: z.string().nullable(),
    lastSyncErrorAt: z.string().nullable(),
    pages: z.array(metaIntegrationPageSchema),
    defaultPage: metaIntegrationPageSchema.nullable(),
    availableBusinesses: z.array(metaBusinessInfoSchema).nullable(),
    availableAdAccounts: z.array(metaAdAccountInfoSchema).nullable(),
    availablePages: z.array(metaPageInfoStoredSchema).nullable(),
});
/** `GET /integrations/meta-ads` — the integration wrapper (null = none). */
export const getMetaIntegrationResponseSchema = z.object({
    integration: metaAdsIntegrationSchema.nullable(),
});
/** A synced Meta lead form. */
export const metaLeadFormSchema = z.object({
    id: z.string(),
    name: z.string(),
    status: z.string(),
    locale: z.string().optional(),
    createdTime: z.string().optional(),
    pageId: z.string().optional(),
});
/** `GET /integrations/meta-ads/lead-forms` — `{ forms }`. */
export const listMetaLeadFormsResponseSchema = z.object({
    forms: z.array(metaLeadFormSchema),
});
/** A connected Google My Business account. */
export const googleMyBusinessAccountSchema = z.object({
    id: z.string(),
    organizationId: z.string(),
    googleAccountEmail: z.string(),
    accountName: z.string(),
    locationId: z.string(),
    locationName: z.string(),
    placeId: z.string(),
    reviewLink: z.string(),
    averageRating: z.number().nullable(),
    /**
     * DB default is `0`, but the column has no `NOT NULL` — a raw update can
     * still leave it `null`, and the service selects it straight through
     * (`list-google-my-business-accounts.service.ts`). `.nullable()` so that
     * survives the interceptor's JSON round-trip (ENG-843).
     */
    totalReviews: z.number().nullable(),
    isActive: z.boolean(),
    lastSyncAt: z.string().nullable(),
    tokenExpiresAt: z.string().nullable(),
    createdAt: z.string(),
    updatedAt: z.string(),
});
/** `GET /integrations/google-business/accounts` — `{ accounts }`. */
export const listGoogleMyBusinessAccountsResponseSchema = z.object({
    accounts: z.array(googleMyBusinessAccountSchema),
});
// ============================================================================
// INSTAGRAM
// ============================================================================
/**
 * An Instagram integration (computed connection view, not the atom): adds a
 * derived `tokenStatus` and the joined `connectedByName`.
 */
export const instagramIntegrationSchema = z.object({
    id: z.string(),
    instagramUserId: z.string().nullable(),
    username: z.string().nullable(),
    name: z.string().nullable(),
    profilePictureUrl: z.string().nullable(),
    accountType: z.string().nullable(),
    isActive: z.boolean(),
    chatbotEnabled: z.boolean(),
    postingOnly: z.boolean(),
    tokenStatus: z.enum(['valid', 'needs_reconnect']),
    connectedByName: z.string().nullable(),
    tokenExpiresAt: z.string().nullable(),
    createdAt: z.string(),
});
/** `GET /integrations/instagram` — the integration wrapper (null = none). */
export const getInstagramIntegrationResponseSchema = z.object({
    integration: instagramIntegrationSchema.nullable(),
});
// ============================================================================
// STRIPE CONNECT (embedded onboarding status)
// ============================================================================
/**
 * Online booking is blocked: active services (or the org default) require
 * payment at booking, but the account cannot charge. Sent only when it
 * applies.
 */
export const bookingPaymentWarningSchema = z.object({
    reason: z.enum(['not_connected', 'inactive', 'charges_disabled']),
    defaultPolicyRequiresPayment: z.boolean(),
    serviceCount: z.number().int().nonnegative(),
    serviceNames: z.array(z.string()),
});
/** `GET /integrations/stripe/account-status` — the Connect account status. */
export const stripeConnectStatusSchema = z.object({
    connected: z.boolean(),
    accountType: z
        .enum(['standard_oauth', 'standard_linked', 'controller'])
        .nullable(),
    chargesEnabled: z.boolean(),
    payoutsEnabled: z.boolean(),
    detailsSubmitted: z.boolean(),
    requirementsCurrentlyDue: z.array(z.string()),
    disabledReason: z.string().nullable(),
    bookingPaymentWarning: bookingPaymentWarningSchema.optional(),
});
// ============================================================================
// SEQUENCES (projection-only — no wired hooks live under this domain's dirs;
// the sequence builder consumes these types via api-client)
// ============================================================================
/** A sequence (automation) row — the atom, verbatim. */
export const sequenceSchema = sequenceAtomSchema;
// ============================================================================
// API KEYS (projection-only — no app hooks; developer settings surface)
// ============================================================================
/** An API key as returned by the list endpoint (the raw `key` is never exposed). */
export const apiKeyListItemSchema = z.object({
    id: z.string(),
    name: z.string().nullable(),
    start: z.string().nullable(),
    prefix: z.string().nullable(),
    enabled: z.boolean(),
    expiresAt: z.string().nullable(),
    createdAt: z.string(),
    lastRequest: z.string().nullable(),
    scopes: z.array(z.string()),
    rateLimitMax: z.number().nullable(),
});
/** `GET /api-keys` — `{ items }`. */
export const apiKeyListResponseSchema = z.object({
    items: z.array(apiKeyListItemSchema),
});
/** `POST /api-keys` — the created key (the plaintext `key` is shown ONCE). */
export const createApiKeyResponseSchema = z.object({
    id: z.string(),
    key: z.string(),
    name: z.string().nullable(),
    expiresAt: z.string().nullable(),
    prefix: z.string().nullable(),
});
/** The full `apikey` row atom (server-side; the plaintext key IS present). */
export const apiKeyRowSchema = apikeyAtomSchema;
// ============================================================================
// TRAINING HUB (projection-only — no wired hooks under this domain's dir)
// ============================================================================
/** A training video row — the atom, verbatim. */
export const trainingVideoSchema = trainingVideoAtomSchema;
/** A user's progress against a training video — the atom, verbatim. */
export const userVideoProgressSchema = userVideoProgressAtomSchema;
/** A training video with the current user's progress (joined projection). */
export const trainingVideoWithProgressSchema = trainingVideoAtomSchema.extend({
    progress: userVideoProgressSchema.nullable(),
});
// ---------------------------------------------------------------------------
// Connection health
// ---------------------------------------------------------------------------
/**
 * One third-party connection and whether we can still act through it.
 *
 * `remedy` is the field consumers branch on, not `state`: it is the difference
 * between offering a reconnect button and telling someone to go and clear a
 * restriction with Meta, which is the only thing that works for an account Meta
 * has locked.
 */
export const connectionHealthItemSchema = z.object({
    provider: z.enum(connectionProviderValues),
    providerLabel: z.string(),
    connectionId: z.string(),
    label: z.string().nullable(),
    state: z.enum(connectionHealthStateValues),
    reasonCode: z.string(),
    remedy: z.enum(connectionHealthRemedyValues),
    detail: z.string().nullable(),
    actionUrl: z.string().nullable(),
    actionLabel: z.string().nullable(),
    videoGuideSlug: z.string().nullable(),
    stateSince: z.string().datetime(),
    checkedAt: z.string().datetime(),
});
/** `GET /integrations/connection-health` and its `POST .../refresh` twin. */
export const connectionHealthResponseSchema = z.object({
    connections: z.array(connectionHealthItemSchema),
    problems: z.array(connectionHealthItemSchema),
    allHealthy: z.boolean(),
    lastCheckedAt: z.string().datetime().nullable(),
});
/**
 * One channel as the code path that uses it sees it (ENG-1944). `record` is
 * the row that path reads; `problem` and `fixStep` say which record is missing
 * or expired and the exact screen and button that fixes it.
 */
export const channelStatusItemSchema = z.object({
    channel: z.enum(connectionChannelValues),
    label: z.string(),
    status: z.enum(channelStatusValues),
    record: z
        .object({
        type: z.enum(channelRecordValues),
        id: z.string(),
        name: z.string().nullable(),
    })
        .nullable(),
    problem: z.string().nullable(),
    fixStep: z.string().nullable(),
    fixAction: z.enum(channelFixActionValues).nullable(),
    actionUrl: z.string().nullable(),
    chatbotEnabled: z.boolean().nullable(),
    lastFailure: z
        .object({ at: z.string().datetime(), message: z.string() })
        .nullable(),
});
/** `GET /integrations/channel-status`. */
export const channelStatusResponseSchema = z.object({
    channels: z.array(channelStatusItemSchema),
});
/** `POST /imports/detect` — which importer a spreadsheet belongs to. */
export const detectImportKindResponseSchema = z.object({
    kind: z.enum(importKindValues).nullable(),
    confidence: z.enum(['high', 'medium', 'low']),
    candidates: z.array(z.object({ kind: z.enum(importKindValues), score: z.number() })),
    headers: z.array(z.string()),
    rowsInFile: z.number(),
});
/** A value an imported record cannot be created without. */
const importRequirementSchema = z.object({
    label: z.string(),
    fields: z.array(z.string()),
    header: z.string(),
});
/** `POST /admin-terminal/organizations/:id/imports` — one importer's outcome. */
export const runImportResponseSchema = z.object({
    kind: z.enum(importKindValues),
    imported: z.number(),
    updated: z.number(),
    skipped: z.number(),
    rowsInFile: z.number(),
    skippedRows: z.number().optional(),
    doubleBooked: z.number().optional(),
    errors: z.array(z.object({
        row: z.number(),
        message: z.string(),
        cells: z.array(z.string()).optional(),
    })),
    importRunId: z.string().optional(),
    createdCount: z.number().optional(),
    /** What each column holds and which values a record needs (ENG-1325). */
    fieldGuide: z
        .object({
        columns: z.array(z.object({
            header: z.string(),
            field: z.string().nullable(),
            label: z.string().nullable(),
            format: z.string().nullable(),
            required: z.boolean(),
        })),
        requirements: z.array(importRequirementSchema),
        missing: z.array(importRequirementSchema),
    })
        .optional(),
});
/** `POST /admin-terminal/organizations/:id/imports/clean-rows` — tidied cells. */
export const cleanImportRowsResponseSchema = z.object({
    rows: z.array(z.array(z.string())),
    changes: z.array(z.object({
        row: z.number(),
        column: z.number(),
        from: z.string(),
        to: z.string(),
    })),
    cleaned: z.boolean(),
});
/** Per-record-type counts, e.g. `{ customers: 120, appointments: 340 }`. */
const importedRecordCountsSchema = z.partialRecord(z.enum(importedRecordTypeValues), z.number());
/** What a wipe removed and what it had to keep (ENG-1325). */
export const importRunWipeSummarySchema = z.object({
    deleted: importedRecordCountsSchema,
    kept: importedRecordCountsSchema,
});
/** `GET /admin-terminal/organizations/:id/imports` — saved admin imports. */
export const importRunListResponseSchema = z.array(z.object({
    id: z.string(),
    kind: z.enum(importKindValues),
    fileName: z.string().nullable(),
    counts: z.object({
        imported: z.number(),
        updated: z.number(),
        skipped: z.number(),
        rowsInFile: z.number(),
    }),
    created: importedRecordCountsSchema,
    createdByName: z.string().nullable(),
    createdAt: z.string(),
    wipedAt: z.string().nullable(),
    wipeSummary: importRunWipeSummarySchema.nullable(),
}));
//# sourceMappingURL=misc.js.map